<script lang="ts">
	/**
	 * `/Account/Invoices` — the paperwork (Design §13, §14).
	 *
	 * **Which document a row offers is decided by the row, never by a store-wide setting** (§4.6).
	 * An invoice carries `documentNumber` if and only if the Store issued it, so an invoice from
	 * before that was turned on keeps pointing at the provider's PDF — which is the document the
	 * customer actually received, and the one their accountant already has.
	 *
	 * Ours is *fetched*, not linked: the endpoint is `requireAuth` and the token travels in a
	 * header, so an `<a href>` would reach it unauthenticated. The provider's hosted page and PDF
	 * are real external links — they leave this origin, they get `ExternalLinkIcon`, and they are
	 * never routed. A `RoutedLink` would be wrong twice over: the router cannot handle an
	 * off-origin URL, and the customer would have no warning that the click leaves the app.
	 *
	 * The status filter sits in a `controlpanel` and re-queries the server rather than filtering
	 * the page in hand: a client-side filter over one page of twenty-five silently hides every
	 * matching invoice on page two.
	 */

	import { onMount } from 'svelte';
	import {
		Button,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell
	} from 'flowbite-svelte';
	import {
		CreditNoteLink,
		EmptyState,
		InfiniteLoadElement,
		InvoiceDocumentLinks,
		PageHeader,
		Panel,
		RefundButton,
		SelectField,
		TableRowsPlaceholder
	} from '../../components';
	import { listAccountInvoices, listCreditNotes } from '../../AppNetwork';
	import { toastError } from '../../stores/toast';
	import { formatAmount } from '../../stores/storefront';
	import {
		INVOICE_STATUS_COPY,
		formatCalendarDate,
		invoicePeriodLabel,
		invoiceReference,
		invoiceStatusLabel,
		paymentLineOf
	} from '../../Utilities';
	import type { AccountCreditNote, AccountInvoice } from '../../AppTypes';

	const PAGE_SIZE = 25;
	/*
	 * Credit notes are far rarer than invoices — one per refund — so the panel shows a page and no
	 * more rather than growing an infinite scroll of its own. A customer with more than this has a
	 * support conversation, not a scrolling problem.
	 */
	const CREDIT_NOTE_PAGE_SIZE = 50;

	/**
	 * The filter, which narrows by the **provider's** status and so is named in its words.
	 *
	 * There is deliberately no "Refunded" option here: a refund is not an invoice status at the
	 * provider — it moves the charge and leaves the invoice `paid` — so the server has nothing to
	 * filter on, and an option that quietly returned every paid invoice would be worse than its
	 * absence. The rows say which ones were refunded.
	 */
	const statusOptions = [
		{ value: '', name: 'Every invoice' },
		{ value: 'open', name: INVOICE_STATUS_COPY.open },
		{ value: 'paid', name: INVOICE_STATUS_COPY.paid },
		{ value: 'draft', name: INVOICE_STATUS_COPY.draft },
		{ value: 'void', name: INVOICE_STATUS_COPY.void },
		{ value: 'uncollectible', name: INVOICE_STATUS_COPY.uncollectible }
	];

	let invoices: AccountInvoice[] = [];
	let total: number = 0;
	let loading: boolean = true;
	let loadingMore: boolean = false;
	let loadError: string = '';
	let status: string = '';

	/*
	 * The two filters that arrive in the URL — `?subscription=` from a subscription's own page,
	 * `?product=` from a link or a bookmark — and are written back to it, so the list a customer is
	 * looking at is the list they get on reload or send to somebody.
	 */
	const initialQuery = new URLSearchParams(window.location.search);
	let subscriptionUUID: string = initialQuery.get('subscription') ?? '';
	let productSlug: string = initialQuery.get('product') ?? '';
	let products: { slug: string; name: string }[] = [];

	$: productOptions = [
		{ value: '', name: 'Every product' },
		...products.map(product => ({ value: product.slug, name: product.name })),
		// A slug from the URL that names nothing invoiced stays selectable, so the control states
		// the filter in force rather than reading "Every product" over an empty list.
		...(productSlug !== '' && !products.some(product => product.slug === productSlug)
			? [{ value: productSlug, name: productSlug }]
			: [])
	];

	/** The product the subscription filter is about, once a row has said which it is. */
	$: subscriptionProduct = invoices.find(invoice => invoice.productName !== null)?.productName ?? null;

	function fnSyncURL(): void {
		const query = new URLSearchParams();
		if (subscriptionUUID !== '') query.set('subscription', subscriptionUUID);
		if (productSlug !== '') query.set('product', productSlug);
		const search = query.toString();
		history.replaceState(history.state, '', `${window.location.pathname}${search === '' ? '' : `?${search}`}`);
	}

	function fnFilterChanged(): void {
		fnSyncURL();
		void fnLoad();
	}

	function fnClearSubscription(): void {
		subscriptionUUID = '';
		fnFilterChanged();
	}

	$: isFiltered = status !== '' || subscriptionUUID !== '' || productSlug !== '';

	/** The paging sentinel. Typed structurally — only its two re-arm methods are used here. */
	let sentinel: { resetVisibilityTracking: () => void; updateState: () => void } | undefined;

	async function fnLoad(reset: boolean = true): Promise<void> {
		// The sentinel is re-armed after every append, and in a tall viewport it is still on
		// screen when that happens — so without this guard one scroll would keep paging past the
		// end of the list, appending the last page over and over.
		if (!reset && (loading || loadingMore || invoices.length >= total)) return;

		if (reset) {
			loading = true;
			invoices = [];
		} else {
			loadingMore = true;
		}
		loadError = '';

		try {
			const result = await listAccountInvoices({
				status: status === '' ? undefined : status,
				subscriptionUUID: subscriptionUUID === '' ? undefined : subscriptionUUID,
				productSlug: productSlug === '' ? undefined : productSlug,
				limit: PAGE_SIZE,
				offset: reset ? 0 : invoices.length
			});
			invoices = reset ? result.items : [...invoices, ...result.items];
			total = result.total;
			products = result.products ?? products;
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
			loadingMore = false;
			sentinel?.resetVisibilityTracking();
			sentinel?.updateState();
		}
	}

	/**
	 * The credit notes (§4.6), loaded **separately and guarded separately**.
	 *
	 * Its own read rather than a field on the invoice rows, for two reasons that point the same way.
	 * A credit note is not always about an invoice — a one-time purchase the provider raised none
	 * for is credited against its own charge — so a column on the invoice table would make exactly
	 * those unreachable. And the two fail for unrelated reasons: an invoice history that has timed
	 * out must not take the refund paperwork down with it, and a customer who has come here after a
	 * refund is looking for this half.
	 */
	let creditNotes: AccountCreditNote[] = [];
	let creditNotesLoading: boolean = true;
	let creditNotesError: string = '';

	async function fnLoadCreditNotes(): Promise<void> {
		creditNotesLoading = true;
		try {
			creditNotes = (await listCreditNotes(CREDIT_NOTE_PAGE_SIZE, 0)).items;
			creditNotesError = '';
		} catch (error) {
			creditNotesError =
				error instanceof Error ? error.message : 'The credit notes could not be loaded';
			creditNotes = [];
		} finally {
			creditNotesLoading = false;
		}
	}

	onMount(() => {
		void fnLoad();
		void fnLoadCreditNotes();
	});

	/**
	 * Through the shared helper rather than formatted here, so this list and the subscription page
	 * cannot answer "what period is this invoice for" two different ways — and so both withhold a
	 * range whose ends render as one date.
	 */
	function periodLine(invoice: AccountInvoice): string {
		return invoicePeriodLabel(invoice);
	}
</script>

<PageHeader title="Invoices">
	<svelte:fragment slot="description">Every invoice raised on this account, with its receipt.</svelte:fragment>
</PageHeader>

<div class="controlpanel mb-6">
	<label class="flex items-center gap-2 text-sm">
		<span class="card-label">Show</span>
		<SelectField
			size="sm"
			class="w-48"
			items={statusOptions}
			bind:value={status}
			on:change={fnFilterChanged}
		/>
	</label>
	{#if products.length > 1 || productSlug !== ''}
		<label class="flex items-center gap-2 text-sm">
			<span class="card-label">Product</span>
			<SelectField
				size="sm"
				class="w-56"
				items={productOptions}
				bind:value={productSlug}
				on:change={fnFilterChanged}
			/>
		</label>
	{/if}
	{#if subscriptionUUID !== ''}
		<!-- Stated, and undoable in one click: a list that arrived narrowed must say so. -->
		<span class="inline-flex items-center gap-2 text-sm rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-200 px-3 py-1">
			<i class="fa-solid fa-filter text-[11px]" aria-hidden="true" />
			One subscription{subscriptionProduct !== null ? `: ${subscriptionProduct}` : ''}
			<button
				type="button"
				class="ml-1 hover:text-primary-600"
				aria-label="Show invoices for every subscription"
				on:click={fnClearSubscription}
			>
				<i class="fa-solid fa-xmark" aria-hidden="true" />
			</button>
		</span>
	{/if}
	{#if loadError.length === 0}
		<span class="text-sm text-default">{total} invoice{total === 1 ? '' : 's'}</span>
	{/if}
</div>

<Panel title="Invoice history" faIcon="file-invoice-dollar">
	{#if loadError.length > 0}
		<EmptyState variant="error" title="Your invoices could not be loaded." description={loadError}>
			<Button color="alternative" size="sm" on:click={() => fnLoad()}>Try again</Button>
		</EmptyState>
	{:else if !loading && invoices.length === 0}
		<EmptyState
			title={!isFiltered ? 'No invoices yet.' : 'No invoices match that filter.'}
			description={!isFiltered
				? 'Invoices appear here as soon as anything is billed.'
				: 'Try showing every invoice instead.'}
		/>
	{:else}
		<Table divClass="relative overflow-x-auto">
			<TableHead>
				<TableHeadCell>Invoice</TableHeadCell>
				<TableHeadCell>Date</TableHeadCell>
				<TableHeadCell>Total</TableHeadCell>
				<TableHeadCell>Status</TableHeadCell>
				<TableHeadCell>Receipt</TableHeadCell>
				<TableHeadCell>Refund</TableHeadCell>
			</TableHead>
			<TableBody>
				{#if loading}
					<TableRowsPlaceholder rows={5} columns={6} />
				{:else}
					{#each invoices as invoice (invoice.uuid)}
						<TableBodyRow>
							<TableBodyCell class="dataTable">
								<div>{invoiceReference(invoice)}</div>
								{#if invoice.productName !== null}
									<div class="text-xs text-default">{invoice.productName}</div>
								{/if}
								{#if periodLine(invoice).length > 0}
									<div class="text-xs text-default">{periodLine(invoice)}</div>
								{/if}
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								{#if invoice.issuedAt !== null}
									{formatCalendarDate(invoice.issuedAt)}
								{:else}
									<span class="text-default">Not issued yet</span>
								{/if}
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								{$formatAmount(invoice.total, invoice.currency) ?? 'Amount unavailable'}
								{#if paymentLineOf(invoice.paymentMethod) !== null}
									<div class="text-xs text-default">{paymentLineOf(invoice.paymentMethod)}</div>
								{/if}
								{#if invoice.status === 'open' && invoice.amountRemaining !== null && invoice.amountRemaining > 0}
									<div class="text-xs text-yellow-600 dark:text-yellow-400">
										{$formatAmount(invoice.amountRemaining, invoice.currency) ?? ''} still owing
									</div>
								{/if}
							</TableBodyCell>
							<TableBodyCell class="dataTable">{invoiceStatusLabel(invoice)}</TableBodyCell>
							<TableBodyCell class="dataTable">
								<!-- Shared with the subscription detail page: the ours-or-theirs rule and
								     the `documentNumber` test live in one place, not two. -->
								<InvoiceDocumentLinks {invoice} />
							</TableBodyCell>
							<TableBodyCell class="dataTable">
								<!-- The eligibility travelled with the row; the control only renders it. -->
								<RefundButton
									invoiceUUID={invoice.uuid}
									eligibility={invoice.refund}
									productName={invoice.productName}
									on:refunded={() => void fnLoad()}
								/>
							</TableBodyCell>
						</TableBodyRow>
					{/each}
				{/if}
			</TableBody>
		</Table>

		{#if !loading && invoices.length < total}
			<InfiniteLoadElement bind:this={sentinel} class="w-full" on:becomeVisible={() => void fnLoad(false)}>
				{#if loadingMore}
					<EmptyState variant="loading" compact title="Loading more…" />
				{/if}
			</InfiniteLoadElement>
		{/if}
	{/if}
</Panel>

<!--
	Under the invoices rather than beside them, because it is read second: a customer arrives here
	looking for an invoice, and looks for the credit note only once they have found the charge it
	reverses. It renders **only when there is something in it** — a store that has never refunded
	anybody should not explain what a credit note is on a page about invoices.

	And only once the answer is known, which is why the loading flag is deliberately *not* in this
	condition. A panel that appears while its read is in flight and then vanishes is a heading and a
	table's worth of height inserted under the invoice list and taken away again — on the common
	case, which is a customer with no refunds, that is the page moving under the reader for no
	reason. The invoice history above carries the page's loading state; this is a secondary panel
	and can simply arrive.
-->
{#if creditNotesError.length > 0 || creditNotes.length > 0}
	<div class="mt-6">
		<Panel title="Credit notes" faIcon="file-circle-minus">
			<svelte:fragment slot="title">
				<span class="text-xs text-default">Issued when a payment is refunded.</span>
			</svelte:fragment>
			{#if creditNotesError.length > 0}
				<EmptyState
					variant="error"
					title="Your credit notes could not be loaded."
					description={creditNotesError}
				>
					<Button
						color="alternative"
						size="sm"
						disabled={creditNotesLoading}
						on:click={() => void fnLoadCreditNotes()}
					>
						{creditNotesLoading ? 'Trying…' : 'Try again'}
					</Button>
				</EmptyState>
			{:else}
				<Table divClass="relative overflow-x-auto">
					<TableHead>
						<TableHeadCell>Credit note</TableHeadCell>
						<TableHeadCell>Date</TableHeadCell>
						<TableHeadCell>Credits</TableHeadCell>
						<TableHeadCell>Total</TableHeadCell>
						<TableHeadCell>Document</TableHeadCell>
					</TableHead>
					<TableBody>
						{#each creditNotes as creditNote (creditNote.uuid)}
							<TableBodyRow>
								<TableBodyCell class="dataTable">
									<div>{creditNote.documentNumber}</div>
									{#if creditNote.productName !== null}
										<div class="text-xs text-default">{creditNote.productName}</div>
									{/if}
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									{formatCalendarDate(creditNote.issuedAt)}
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									<!-- The reference **as the document states it**, not one derived here:
									     it is what the customer's own PDF says, and a list naming a
									     different code than the paper behind it cannot be reconciled. -->
									{creditNote.creditedReference ?? '—'}
								</TableBodyCell>
								<!-- `tabular-nums` for the reason every money column carries it: without
								     it a narrow column may break a total across two lines, and a figure
								     rendered "-€58.0 / 0" still looks like a number. -->
								<TableBodyCell class="dataTable tabular-nums">
									{$formatAmount(creditNote.total, creditNote.currency) ?? 'Amount unavailable'}
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									<CreditNoteLink
										uuid={creditNote.uuid}
										documentNumber={creditNote.documentNumber}
									/>
								</TableBodyCell>
							</TableBodyRow>
						{/each}
					</TableBody>
				</Table>
			{/if}
		</Panel>
	</div>
{/if}
